import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CustomText, LessonType, SentenceType, StatusType, TaskType } from "App.types";
import { HistoryEditor, withHistory } from "slate-history";
import { BaseEditor, BaseElement, createEditor, Transforms } from "slate";
import { Button, Flex, Popconfirm, Popover, Space, Tag, Tooltip } from "antd";
import API from "Api";
import { delay } from "lodash";
import cx from "classnames";
import { getCompletedSlate, withCustomLogic } from "App.helpers";
import { BulbOutlined, SendOutlined, WarningOutlined } from "@ant-design/icons";
import SentenceMaskedLeaf from "../SentenceLeaf";
import { useOnCheck } from "../Helpers/useOnCheck";
import { usePressEnter } from "../Helpers/usePressEnter";
import { isMobile } from "react-device-detect";

import styles from "./DialogTaskWriting.module.scss";
import { NotifyContext } from "App";

const initialValue = [{ children: [{ text: "" }] }];

export type CustomElement = { children: CustomText[] } & BaseElement;

declare module "slate" {
  export interface CustomTypes {
    Editor: ReactEditor & BaseEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const getPlainText = (nodes: any) => {
  return nodes
    .map((node: any) => {
      if (node.text) {
        return node.text;
      }
      if (node.children) {
        return getPlainText(node.children);
      }
      return "";
    })
    .join("")
    .trim();
};

const DialogTaskWriting: FC<{
  onHint?: () => any;
  onChange?: (v: string) => any;
  sentence: SentenceType;
  lesson: LessonType;
  onTaskComplete: Function;
  taskDescription?: string;
  storyId?: number;
}> = ({
  sentence,
  taskDescription,
  storyId,
  sentence: { id, text, tags, translate, alternatives, markers, phrases, isLeft },
  onTaskComplete,
  onChange,
  onHint,
  lesson,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [activeLeaf, setActiveLeaf] = useState<number | null>(null);
  const [hints, setHints] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState<string>();

  const editor = useMemo(() => withReact(withHistory(withCustomLogic(createEditor()))), []);

  const noTranslate = markers.includes("noTranslate");

  const isCompleted = status === StatusType.Completed;

  const setInitialState = useCallback(
    (withFocus: boolean = true) => {
      editor.children.forEach(() => {
        Transforms.delete(editor, { at: [0] });
      });

      editor.children = [];

      Transforms.insertNodes(editor, initialValue, { select: withFocus });
      if (withFocus) {
        try {
          ReactEditor.focus(editor);
        } catch (e) {}
      }
    },
    [editor],
  );

  // initial
  useEffect(() => {
    setActiveLeaf(null);
    setStatus(StatusType.Editing);
    setInitialState(true);
    const hintTags = tags.filter((t, idx) => t.isHint && !phrases.some((ph) => ph.positionsIdx.includes(idx))).map((t) => t.lemma);
    const hintPhrases = phrases.filter((ph) => ph.positionsIdx.some((idx) => tags[idx].isHint)).map((ph) => ph.text);

    setHints([...hintTags, ...hintPhrases]);
  }, [sentence.id, setInitialState, tags, phrases]);

  // audio transcript
  // useAudioTranscript({ setActiveLeaf, sentence, audio, transcripts });

  const renderLeaf = useCallback(
    (props: any) => (
      <SentenceMaskedLeaf
        underlined={props.leaf.audioIdx === (activeLeaf ?? -1)}
        showErrors={!["editing", "completed", "loading", "isRecording", ""].includes(status)}
        {...props}
      />
    ),
    [status, activeLeaf],
  );

  const onComplete = useCallback(() => {
    setStatus(StatusType.Completed);
    onTaskComplete();
  }, [onTaskComplete]);

  const notifyApi = useContext(NotifyContext);

  const isTextTask = sentence.tasks.includes(TaskType.Text);

  const onCheck = useOnCheck({
    editor,
    activeType: TaskType.Translate,
    text,
    tags,
    phrases,
    id,
    lesson,
    alternatives,
    onComplete,
    setStatus,
  });

  const onTextTaskCheck = useCallback(async () => {
    const userAnswer = getPlainText(editor.children);

    if (!userAnswer) return notifyApi?.info({ message: "выполни задание!" });

    setIsLoading(true);
    setInfo("");
    const { comments, wrong } = await API.sentence
      .check(sentence.id, { answer: userAnswer, lessonId: lesson.id, storyId, taskDescription })
      .finally(() => setIsLoading(false));
    notifyApi.destroy();

    if (wrong) {
      setInfo(comments);
      notifyApi.error({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
    } else {
      setInfo("");
      notifyApi.success({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 5 });
      onComplete();
    }
  }, [editor.children, lesson.id, notifyApi, onComplete, sentence.id, storyId, taskDescription]);

  const onHintAnswer = async () => {
    const text = editor.children[0]?.children?.map((el: CustomText) => el.text).join("");
    API.event.save({ text, type: "hint", task: TaskType.Translate, lesson: { id: lesson.id }, sentence });

    const status = await onCheck(text, true);
    const wrongIdx = editor.children[0]?.children.findIndex((child: CustomText) => child.status === "wrong");
    if (wrongIdx >= 0) {
      Transforms.insertText(editor, "", { at: [0, wrongIdx] });
      await onCheck("", true);
    }

    if (status) {
      const hasError = editor.children[0]?.children.every((el: CustomText, idx: number) => {
        if (["missed", "lemma", "typo"].includes(el.status || "")) {
          //  setTry(true);
          Transforms.setNodes(editor, { status: "success" }, { at: [0, idx] });
          Transforms.insertText(editor, el.word || el.answer || "", { at: [0, idx] });
          setActiveLeaf(el.idx || null);
          delay(() => setActiveLeaf(null), 2000);
          return false;
        }

        if (el.status === "order") {
          editor.children.forEach(() => {
            Transforms.delete(editor, { at: [0] });
          });

          editor.children = [];
          Transforms.insertNodes(editor, [{ children: getCompletedSlate(tags, text) }]);
          return false;
        }
        return true;
      });

      if (hasError) {
        // setTry();
      }

      try {
        ReactEditor.focus(editor);
      } catch (e) {}
    }
  };

  usePressEnter({ isCompleted, onCheck: isTextTask ? onTextTaskCheck : onCheck });

  return (
    <Flex className={styles.dialogTaskWriting}>
      {info && (
        <Popover title={<div dangerouslySetInnerHTML={{ __html: info }} />}>
          <Button danger type={"text"} icon={<WarningOutlined />} />
        </Popover>
      )}
      <div className={cx(styles.slate)}>
        <Tooltip title={status === StatusType.Completed ? translate : ""}>
          <div
            className={cx(styles.slate_wrapper, {
              [styles.slate_wrapper__left]: isLeft,
              [styles.slate_wrapper__completed]: status === StatusType.Completed,
            })}
          >
            <form spellCheck="false">
              <Slate editor={editor} initialValue={initialValue} onChange={(v) => onChange?.(getPlainText(v))}>
                <Editable
                  className={styles.textArea}
                  readOnly={isCompleted}
                  onKeyDown={() => setStatus(StatusType.Editing)}
                  renderLeaf={renderLeaf}
                  //renderElement={(props) => <StoryElement isActive={activeSent === props.element.id} play={play} {...props} />}
                />
              </Slate>
            </form>
            {!isCompleted && (
              <Button
                loading={isLoading}
                onClick={() => (isTextTask ? onTextTaskCheck() : onCheck())}
                size={"middle"}
                className={cx(styles.btnHint)}
                type={"text"}
                icon={<SendOutlined />}
              />
            )}

            {!isCompleted && !isTextTask && (
              <Popconfirm title={"Дать подсказку?"} onConfirm={onHint || onHintAnswer}>
                <Button
                  style={{ bottom: isMobile ? 4 : 0 }}
                  size={isMobile ? "middle" : undefined}
                  type={"text"}
                  icon={<BulbOutlined />}
                  className={cx(styles.bulb, { [styles.bulb__right]: !isLeft })}
                />
              </Popconfirm>
            )}
          </div>
        </Tooltip>

        {!noTranslate && !isCompleted && <div className={styles.translate}>{translate}</div>}
      </div>

      <div className={styles.bottom}>
        {!isCompleted && (
          <Space>
            {hints.map((hint) => (
              <Tag style={{ fontSize: 16 }} bordered={false} key={hint}>
                {hint}
              </Tag>
            ))}
          </Space>
        )}
      </div>
    </Flex>
  );
};

export default DialogTaskWriting;
